import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PenLine, CheckCircle, AlertCircle, Mail, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";

type OrderStatus = "pending" | "paid" | "completed" | "email_failed" | "unknown";

const Writing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [orderStatus, setOrderStatus] = useState<OrderStatus>("paid");
  const [orderId, setOrderId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(true);
  const [city, setCity] = useState<string | null>(null);

  // Get order ID from URL or localStorage
  useEffect(() => {
    const urlOrderId = searchParams.get("order_id");
    const storedOrderId = localStorage.getItem("eunice_order_id");
    
    if (urlOrderId) {
      setOrderId(urlOrderId);
      localStorage.setItem("eunice_order_id", urlOrderId);
      // Clean up URL params
      navigate("/writing", { replace: true });
    } else if (storedOrderId) {
      setOrderId(storedOrderId);
    } else {
      // No order ID found - redirect to home
      navigate("/", { replace: true });
    }
  }, [searchParams, navigate]);

  // Poll for order status using secure RPC function
  const checkOrderStatus = useCallback(async () => {
    if (!orderId || !isPolling) return;

    try {
      // Use secure RPC function - only returns status & city, no sensitive data
      // Time-limited to orders created in the last 24 hours
      const { data, error } = await supabase
        .rpc('get_order_status', { order_uuid: orderId });

      if (error || !data || data.length === 0) {
        // Order not found or expired (>24h old) - stop polling
        return;
      }

      const order = data[0];
      setCity(order.city);
      
      const status = order.status as OrderStatus;
      setOrderStatus(status);

      // Stop polling if completed or failed
      if (status === "completed" || status === "email_failed") {
        setIsPolling(false);
        // Clear stored order ID on completion
        if (status === "completed") {
          localStorage.removeItem("eunice_order_id");
        }
      }
    } catch {
      // Silently handle polling errors - will retry on next interval
    }
  }, [orderId, isPolling]);

  // Polling effect
  useEffect(() => {
    if (!orderId) return;

    // Initial check
    checkOrderStatus();

    // Poll every 5 seconds
    const pollInterval = setInterval(() => {
      if (isPolling) {
        checkOrderStatus();
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [orderId, isPolling, checkOrderStatus]);

  // Track elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeElapsed(prev => {
        const newTime = prev + 1;
        // Stop polling after 10 minutes (600 seconds)
        if (newTime >= 600 && isPolling) {
          setIsPolling(false);
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isPolling]);

  const handleRequestAnother = () => {
    localStorage.removeItem("eunice_order_id");
    navigate("/");
    setTimeout(() => {
      document.getElementById("order")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleRetry = () => {
    setIsPolling(true);
    setTimeElapsed(0);
    checkOrderStatus();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const cityDisplay = city ? city.charAt(0).toUpperCase() + city.slice(1) : "your destination";

  // Render based on order status
  const renderContent = () => {
    // Letter sent successfully
    if (orderStatus === "completed") {
      return (
        <div className="max-w-xl text-center opacity-0 animate-fade-up">
          <div className="inline-flex items-center justify-center mb-8">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" aria-hidden="true" />
            </div>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-foreground leading-tight mb-4 sm:mb-6">
            Your letter has arrived!
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-charcoal-light mb-4 font-sans px-4">
            Eunice's letter from {cityDisplay} is in your inbox.
          </p>

          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-8 font-sans">
            <Mail className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>Check your spam folder if you don't see it.</span>
          </div>

          <Button 
            variant="eunice" 
            size="lg" 
            onClick={handleRequestAnother}
            className="min-h-[48px] min-w-[200px]"
          >
            Request Another Letter
          </Button>
        </div>
      );
    }

    // Email failed
    if (orderStatus === "email_failed") {
      return (
        <div className="max-w-xl text-center opacity-0 animate-fade-up">
          <div className="inline-flex items-center justify-center mb-8">
            <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-10 w-10 text-destructive" aria-hidden="true" />
            </div>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-foreground leading-tight mb-4 sm:mb-6">
            Something went wrong
          </h1>

          <p className="text-base sm:text-lg text-charcoal-light mb-6 font-sans px-4">
            We couldn't deliver your letter. This sometimes happens with email filtering.
          </p>

          <div className="bg-muted/50 rounded-lg p-4 sm:p-6 mb-8 mx-4">
            <p className="text-sm text-foreground mb-3">
              Please contact us and we'll send your letter manually:
            </p>
            <a 
              href="mailto:letters@eunice.world?subject=Letter%20Delivery%20Issue" 
              className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
            >
              <Mail className="h-4 w-4" aria-hidden="true" />
              letters@eunice.world
            </a>
          </div>

          <Button 
            variant="eunice-outline" 
            size="lg" 
            onClick={handleRequestAnother}
            className="min-h-[48px]"
          >
            Return Home
          </Button>
        </div>
      );
    }

    // Still processing (paid status or unknown)
    const showLongWait = timeElapsed >= 300; // 5 minutes
    const showTimeout = timeElapsed >= 600; // 10 minutes

    return (
      <div className="max-w-xl text-center opacity-0 animate-fade-up">
        {/* Animated pen icon */}
        <div className="inline-flex items-center justify-center mb-8 sm:mb-10">
          <div className="relative">
            <PenLine 
              className="h-12 w-12 sm:h-16 sm:w-16 text-primary animate-writing" 
              strokeWidth={1.5}
              aria-hidden="true"
            />
            {/* Animated ink dots */}
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-1" aria-hidden="true">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-dot-1" />
              <span className="w-1.5 h-1.5 rounded-full bg-primary/30 animate-dot-2" />
              <span className="w-1.5 h-1.5 rounded-full bg-primary/20 animate-dot-3" />
            </div>
          </div>
        </div>

        {/* Success indicator */}
        <div className="flex items-center justify-center gap-2 mb-4 sm:mb-6">
          <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" aria-hidden="true" />
          <span className="text-xs sm:text-sm text-green-700 font-sans">Payment successful</span>
        </div>

        {/* Main headline */}
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-foreground leading-tight mb-4 sm:mb-6 px-4">
          Eunice is writing your letter...
        </h1>

        {/* Subtext */}
        <p className="text-base sm:text-lg md:text-xl text-charcoal-light mb-4 font-sans px-4">
          {city && `A letter from ${cityDisplay} is on its way.`}
          {!city && "Your personalized letter is being crafted."}
        </p>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-3 mb-6" aria-live="polite">
          <div className="flex gap-1" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <div 
                key={i}
                className="w-2 h-2 rounded-full bg-primary/60 animate-pulse"
                style={{ animationDelay: `${i * 200}ms` }}
              />
            ))}
          </div>
          <span className="text-sm text-muted-foreground font-sans">
            {formatTime(timeElapsed)}
          </span>
        </div>

        {/* Email note */}
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-6 sm:mb-8 font-sans px-4">
          <Mail className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>Usually arrives within 5 minutes. Check spam folder too.</span>
        </div>

        {/* Long wait warning */}
        {showLongWait && !showTimeout && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 sm:mb-8 mx-4 animate-fade-up" role="alert">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="text-left">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  Taking longer than expected
                </p>
                <p className="text-sm text-amber-700">
                  Eunice is still crafting your letter. We'll keep checking.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Timeout - stopped polling */}
        {showTimeout && (
          <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6 sm:mb-8 mx-4 animate-fade-up">
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-foreground text-center">
                Still working on your letter. You can close this page—we'll email you when it's ready.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetry}
                className="min-h-[40px]"
              >
                <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
                Keep Checking
              </Button>
            </div>
          </div>
        )}

        {/* CTA button */}
        <Button 
          variant="eunice-outline" 
          size="lg" 
          onClick={handleRequestAnother}
          className="min-h-[48px]"
        >
          Request Another Letter
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 sm:px-6 py-8 sm:py-12">
      <SEO
        title="Writing Your Letter - Eunice"
        description="Eunice is crafting your personalized travel letter."
        canonical="/writing"
        noindex={true}
      />
      {renderContent()}
    </div>
  );
};

export default Writing;
